package or.garden.manager.domain

import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.isSuccess
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.ApiResponse
import or.garden.manager.domain.model.BedData

internal class GardenApi {
    suspend fun getBeds(): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get(
            "${GardenHttpClient.baseUrl}/beds"
        )
    }

    suspend fun getById(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.get(
            "${GardenHttpClient.baseUrl}/beds/$id"
        )
    }

    suspend fun deleteById(id: Int): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.delete(
            "${GardenHttpClient.baseUrl}/beds/$id"
        )
    }

    suspend fun addBed(bedData: BedData): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.post(
            "${GardenHttpClient.baseUrl}/beds"
        ) {
            setBody(bedData)
        }
    }

    suspend fun updateBed(bedData: BedData): Result<HttpResponse> = runCatching {
        return@runCatching GARDEN_CLIENT.put(
            "${GardenHttpClient.baseUrl}/beds/${bedData.id}"
        ) {
            setBody(bedData)
        }
    }
}

private data object GardenRepository {
    fun getBeds(): Flow<ResultState<ApiResponse<List<BedData>>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = GardenApi()
                .getBeds()
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("An error occurred. Please try again!"))
        }
    }

    fun getById(id: Int): Flow<ResultState<ApiResponse<BedData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = GardenApi()
                .getById(id)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("An error occurred. Please try again!"))
        }
    }

    fun deleteById(id: Int): Flow<ResultState<ApiResponse<String>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = GardenApi()
                .deleteById(id)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("An error occurred. Please try again!"))
        }
    }

    fun addBed(bedData: BedData): Flow<ResultState<ApiResponse<BedData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = GardenApi()
                .addBed(bedData)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            emit(ResultState.Error("An error occurred. Please try again!"))
        }
    }

    fun updateBed(bedData: BedData): Flow<ResultState<ApiResponse<BedData>>> = flow {
        emit(ResultState.Loading)
        delay(GardenHttpClient.apiDelayMillis)
        try {
            val response = GardenApi()
                .updateBed(bedData)
                .getOrNull()
            if (response != null && response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response?.body<ApiResponse<String>>()?.message))
            }
        } catch (e: Exception) {
            e.printStackTrace()
            emit(ResultState.Error("An error occurred. Please try again!"))
        }
    }
}

data object GardenService {
    fun getBeds(result: (ResultState<ApiResponse<List<BedData>>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            GardenRepository
                .getBeds()
                .collect {
                    result(it)
                }
        }
    }

    fun addBed(bedData: BedData, result: (ResultState<ApiResponse<BedData>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            GardenRepository
                .addBed(bedData)
                .collect {
                    result(it)
                }
        }
    }

    fun updateBed(bedData: BedData, result: (ResultState<ApiResponse<BedData>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            GardenRepository
                .updateBed(bedData)
                .collect {
                    result(it)
                }
        }
    }

    fun fetchById(id: Int, result: (ResultState<ApiResponse<BedData>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            GardenRepository
                .getById(id)
                .collect {
                    result(it)
                }
        }
    }

    fun deleteBed(id: Int, result: (ResultState<ApiResponse<String>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            GardenRepository
                .deleteById(id)
                .collect {
                    result(it)
                }
        }
    }
}
